import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import EmailVerificationBanner from '../pages/EmailVerificationBanner';
<EmailVerificationBanner/>

const API = process.env.REACT_APP_API_URL || 'https://localhost:8000';

const TIER_ICONS  = { free:'🌱', ai_tools:'🤖', ai_tools_plus:'🚀', ai_premium:'👑' };

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.50)', dim:'rgba(255,255,255,0.50)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  red:'#e74c3c', yellow:'#f1c40f', orange:'#e67e22',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
  mono:'"JetBrains Mono","Fira Code",monospace',
};

const TIER_COLORS = { free:T.muted, ai_tools:T.cyan, ai_tools_plus:T.yellow, ai_premium:'#b78cff' };
const TIER_LABELS = { free:'Free', ai_tools:'AI Tool', ai_tools_plus:'AI + Chrome Ext', ai_premium:'AI Premium' };

function Badge({ text, color=T.blue }) {
  return (
    <span style={{
      padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700,
      background:`${color}18`, border:`1px solid ${color}40`, color,
      textTransform:'uppercase', letterSpacing:'.05em',
    }}>{text}</span>
  );
}

function RowLine({ label, value, highlight }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${T.border}`, fontSize:13 }}>
      <span style={{ color:T.muted }}>{label}</span>
      <span style={{ color:highlight||T.white }}>{value}</span>
    </div>
  );
}

export function BillingTab({ subscription, payments, onCancel, onResume, onPortal, loading }) {
  const tier  = subscription?.tier || 'free';
  const color = TIER_COLORS[tier];

  return (
    <div>
      {/* Current plan */}
      <div style={{
        padding:'18px 20px', marginBottom:16,
        background:T.bg2, border:`1px solid ${color}35`,
        borderRadius:12, boxShadow:`0 0 24px ${color}0a`,
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:12, color:T.dim, marginBottom:6, textTransform:'uppercase', letterSpacing:'.08em' }}>Current Plan</div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ fontSize:17, fontWeight:800, color:T.white }}>{TIER_LABELS[tier]}</div>
              <Badge text={subscription?.status || 'active'} color={subscription?.status==='active'?T.green:T.yellow}/>
            </div>
            {subscription?.cancel_at_period_end && (
              <div style={{ fontSize:12, color:T.orange, marginBottom:6 }}>
                ⚠ Cancels on {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '—'}
              </div>
            )}
            {subscription?.current_period_end && tier !== 'free' && (
              <div style={{ fontSize:12, color:T.dim }}>
                Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
{subscription?.status === 'trialing' && subscription?.trial_ends_at && (
  <div style={{
    padding:'12px 16px', marginBottom:16, borderRadius:9,
    background:'rgba(0,200,240,.08)', border:'1px solid rgba(0,200,240,.25)',
    display:'flex', justifyContent:'space-between', alignItems:'center',
    flexWrap:'wrap', gap:10,
  }}>
    <div>
      <div style={{ fontSize:13.5, fontWeight:700, color:'#dce6fa' }}>
        ⏱ Free trial — {Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - Date.now()) / 86400000))} days remaining
      </div>
      <div style={{ fontSize:12, color:'rgba(255,255,255,0.50)', marginTop:3 }}>
        5 AI requests/day · Trial ends {new Date(sub.trial_ends_at).toLocaleDateString()}
      </div>
    </div>
    <a href="/pricing" style={{
      padding:'7px 16px', borderRadius:7,
      background:'linear-gradient(135deg,#00c8f0,#4d8fff)',
      color:'#07080c', fontSize:12.5, fontWeight:700,
      textDecoration:'none',
    }}>
      Upgrade now →
    </a>
  </div>
)}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {/* Stripe portal — update card, view invoices */}
            {tier !== 'free' && (
              <button onClick={onPortal} disabled={loading} style={{
                padding:'8px 16px', borderRadius:8, cursor:'pointer',
                border:`1px solid ${T.borderH}`, background:T.surf,
                color:T.white, fontSize:12.5, fontFamily:T.sans, fontWeight:600,
                display:'flex', alignItems:'center', gap:6,
              }}>
                💳 Manage Billing
              </button>
            )}

            {/* Resume if scheduled to cancel */}
            {subscription?.cancel_at_period_end && (
              <button onClick={onResume} disabled={loading} style={{
                padding:'8px 16px', borderRadius:8, cursor:'pointer',
                border:`1px solid ${T.green}40`, background:T.green+'10',
                color:T.green, fontSize:12.5, fontFamily:T.sans, fontWeight:600,
              }}>
                Resume Plan
              </button>
            )}

            {/* Cancel */}
            {tier !== 'free' && !subscription?.cancel_at_period_end && (
              <button onClick={onCancel} disabled={loading} style={{
                padding:'8px 16px', borderRadius:8, cursor:'pointer',
                border:`1px solid ${T.red}40`, background:T.red+'0a',
                color:T.red, fontSize:12.5, fontFamily:T.sans, fontWeight:600,
              }}>
                Cancel Plan
              </button>
            )}

            {/* Upgrade */}
            <Link to="/pricing" style={{
              padding:'8px 16px', borderRadius:8,
              border:`1px solid ${color}50`, background:`${color}12`,
              color, fontSize:12.5, fontFamily:T.sans, fontWeight:600,
              textDecoration:'none',
            }}>
              {tier === 'free' ? 'Upgrade ↑' : 'Change Plan'}
            </Link>
          </div>
        </div>
      </div>

      {/* Stripe portal info */}
      {tier !== 'free' && (
        <div style={{
          padding:'12px 14px', marginBottom:18,
          background:'rgba(77,143,255,.06)', border:`1px solid rgba(77,143,255,.15)`,
          borderRadius:9, fontSize:12.5, color:T.muted, lineHeight:1.65,
        }}>
          <strong style={{ color:T.white }}>💳 To update your payment method or download invoices</strong> — click
          {' '}<button onClick={onPortal} style={{ background:'none', border:'none', color:T.cyan, cursor:'pointer', fontSize:12.5, padding:0, fontFamily:T.sans }}>
            Manage Billing
          </button>{' '}
          above. This opens the secure Stripe customer portal.
        </div>
      )}

      {/* Payment history */}
      <div style={{ fontSize:14, fontWeight:700, color:T.white, marginBottom:12 }}>Payment History</div>
      {payments.length === 0 ? (
        <div style={{ fontSize:13, color:T.dim, textAlign:'center', padding:'24px 0' }}>
          {tier === 'free' ? 'No payments yet — upgrade to see billing history.' : 'No payments recorded yet.'}
        </div>
      ) : (
        <div>
          {payments.map(p => (
            <div key={p.id} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'10px 14px', marginBottom:6,
              background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8,
              flexWrap:'wrap', gap:8,
            }}>
              <div>
                <div style={{ fontSize:13, color:T.white }}>{p.plan_name || p.description}</div>
                <div style={{ fontSize:11.5, color:T.dim }}>{new Date(p.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <span style={{ fontFamily:T.mono, fontSize:13, color:T.white }}>{p.amount_display}</span>
                <Badge
                  text={p.status}
                  color={p.status==='succeeded'?T.green:p.status==='pending'?T.yellow:T.red}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



  
function StatCard({ label, value, sub, color=T.cyan }) {
  return (
    <div style={{
      padding:'16px', background:T.bg2, border:`1px solid ${T.border}`,
      borderRadius:10, flex:1, minWidth:140,
    }}>
      <div style={{ fontSize:11, color:T.dim, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color, fontFamily:T.mono }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:T.dim, marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function SectionHead({ title, action }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
      <h2 style={{ fontSize:15, fontWeight:700, color:T.white }}>{title}</h2>
      {action}
    </div>
  );
}



/* ─── CURRENT PLAN CARD ─────────────────────────────────────────────── */
function CurrentPlanSection({ subscription, onCancel, onResume, loading }) {
  const tier     = subscription?.tier || 'free';
  const color    = TIER_COLORS[tier];
  const daysLeft = subscription?.days_remaining;

  return (
    <div style={{
      padding:'20px', background:T.bg2, border:`1px solid ${color}35`,
      borderRadius:12, marginBottom:20,
      boxShadow:`0 0 30px ${color}10`,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <span style={{ fontSize:24 }}>{TIER_ICONS[tier]}</span>
            <div>
              <div style={{ fontSize:18, fontWeight:800, color:T.white }}>{TIER_LABELS[tier]}</div>
              <div style={{ fontSize:12, color:T.muted }}>
                {subscription?.status === 'active' ? 'Active' : subscription?.status}
                {subscription?.cancel_at_period_end && ' · Cancels at period end'}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {subscription?.removes_ads     && <Badge text="No Ads"    color={T.blue}/>}
            {subscription?.allows_ai_tools && <Badge text="AI Tools"  color={T.cyan}/>}
            {subscription?.allows_chrome_extension && <Badge text="Chrome Ext" color={T.yellow}/>}
            {subscription?.allows_api_key  && <Badge text="API Access"color={T.yellow}/>}
            {tier === 'free'               && <Badge text="Free"      color={T.muted}/>}
          </div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
          {daysLeft !== null && daysLeft !== undefined && tier !== 'free' && (
            <div style={{ fontSize:12, color:T.muted, textAlign:'right' }}>
              {subscription?.current_period_end && (
                <>Period ends {new Date(subscription.current_period_end).toLocaleDateString()}<br/></>
              )}
              <span style={{ color:daysLeft<7?T.red:T.muted }}>{daysLeft} days remaining</span>
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            {tier !== 'free' && !subscription?.cancel_at_period_end && (
              <button onClick={onCancel} disabled={loading} style={{
                padding:'7px 14px', borderRadius:7, border:`1px solid ${T.red}40`,
                background:T.red+'10', color:T.red, fontSize:12, fontFamily:T.sans,
                cursor:'pointer', fontWeight:600,
              }}>
                Cancel Plan
              </button>
            )}
            {subscription?.cancel_at_period_end && (
              <button onClick={onResume} disabled={loading} style={{
                padding:'7px 14px', borderRadius:7, border:`1px solid ${T.green}40`,
                background:T.green+'10', color:T.green, fontSize:12, fontFamily:T.sans,
                cursor:'pointer', fontWeight:600,
              }}>
                Resume Plan
              </button>
            )}
            <Link to="/pricing" style={{
              padding:'7px 14px', borderRadius:7,
              border:`1px solid ${color}50`, background:`${color}15`,
              color, fontSize:12, fontFamily:T.sans, fontWeight:600,
              textDecoration:'none',
            }}>
              {tier === 'free' ? 'Upgrade ↑' : 'Change Plan'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── API KEYS SECTION ──────────────────────────────────────────────── */
function APIKeysSection({ apiKeys, allowsApiKey, apiFetch, onRefresh }) {
  const [newKeyName,  setNewKeyName]  = useState('');
  const [createdKey,  setCreatedKey]  = useState(null);
  const [creating,    setCreating]    = useState(false);
  const [revoking,    setRevoking]    = useState(null);
  const [error,       setError]       = useState('');
  const [copiedKey,   setCopiedKey]   = useState(false);

  const createKey = async () => {
    setCreating(true); setError(''); setCreatedKey(null);
    try {
      const res  = await apiFetch('/api/subscriptions/api-keys/', {
        method: 'POST', body: JSON.stringify({ name: newKeyName || 'Default Key' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create key');
      setCreatedKey(data.api_key);
      setNewKeyName('');
      onRefresh();
    } catch(e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const revokeKey = async (keyId) => {
    setRevoking(keyId);
    try {
      await apiFetch(`/api/subscriptions/api-keys/${keyId}/`, { method: 'DELETE' });
      onRefresh();
    } catch {}
    setRevoking(null);
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  if (!allowsApiKey) {
    return (
      <div style={{ padding:'20px', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, textAlign:'center' }}>
        <div style={{ fontSize:24, marginBottom:8 }}>🔑</div>
        <div style={{ fontSize:14, fontWeight:600, color:T.white, marginBottom:6 }}>API Key Access</div>
        <div style={{ fontSize:13, color:T.muted, marginBottom:14 }}>Available on the Full Bundle plan</div>
        <Link to="/pricing" style={{
          padding:'8px 18px', borderRadius:8, border:`1px solid ${T.yellow}50`,
          background:T.yellow+'15', color:T.yellow, fontSize:13, fontWeight:600,
          textDecoration:'none', fontFamily:T.sans,
        }}>Upgrade to Full Bundle →</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Create new key */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <input
          value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
          placeholder="Key name (e.g. Production)"
          style={{
            flex:1, minWidth:180, padding:'8px 12px',
            background:T.bg2, border:`1px solid ${T.border}`,
            borderRadius:7, color:T.white, fontSize:13, fontFamily:T.sans, outline:'none',
          }}
        />
        <button onClick={createKey} disabled={creating} style={{
          padding:'8px 16px', borderRadius:7, border:`1px solid ${T.cyan}50`,
          background:T.cyan+'15', color:T.cyan, fontSize:13, fontWeight:600,
          fontFamily:T.sans, cursor:'pointer',
        }}>
          {creating ? '…' : '+ Create Key'}
        </button>
      </div>

      {error && <div style={{ fontSize:12, color:T.red, marginBottom:10 }}>{error}</div>}

      {/* Newly created key — show once */}
      {createdKey?.full_key && (
        <div style={{
          padding:'14px', marginBottom:14, borderRadius:9,
          background:T.green+'10', border:`1px solid ${T.green}40`,
        }}>
          <div style={{ fontSize:12, color:T.green, fontWeight:700, marginBottom:8 }}>
            ✓ Key created — save it now, it won't be shown again
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <code style={{ flex:1, fontSize:12, fontFamily:T.mono, color:T.white, wordBreak:'break-all', background:T.bg, padding:'8px 10px', borderRadius:6, border:`1px solid ${T.border}` }}>
              {createdKey.full_key}
            </code>
            <button onClick={() => copyKey(createdKey.full_key)} style={{
              padding:'7px 12px', borderRadius:6, border:`1px solid ${T.border}`,
              background:'transparent', color: copiedKey ? T.green : T.muted,
              fontSize:12, fontFamily:T.sans, cursor:'pointer', whiteSpace:'nowrap',
            }}>
              {copiedKey ? '✓ Copied' : '⧉ Copy'}
            </button>
          </div>
          <div style={{ fontSize:11, color:T.muted, marginTop:6 }}>
            Use as: <code style={{ fontFamily:T.mono, color:T.dim }}>Authorization: Api-Key {createdKey.full_key?.slice(0,16)}…</code>
          </div>
        </div>
      )}

      {/* Key list */}
      {apiKeys.length === 0 && !createdKey && (
        <div style={{ fontSize:13, color:T.dim, textAlign:'center', padding:'20px 0' }}>
          No API keys yet. Create one above.
        </div>
      )}
      {apiKeys.map(key => (
        <div key={key.id} style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'10px 14px', marginBottom:6,
          background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8,
          flexWrap:'wrap', gap:8,
        }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:T.white }}>{key.name}</div>
            <div style={{ fontSize:11.5, color:T.dim, fontFamily:T.mono }}>
              {key.key_prefix}••••••••
              {key.last_used && ` · Last used ${new Date(key.last_used).toLocaleDateString()}`}
              {' · '}{key.requests_total} total requests
            </div>
          </div>
          <button onClick={() => revokeKey(key.id)} disabled={revoking===key.id} style={{
            padding:'5px 12px', borderRadius:6, border:`1px solid ${T.red}40`,
            background:T.red+'10', color:T.red, fontSize:11.5, fontFamily:T.sans, cursor:'pointer',
          }}>
            {revoking===key.id ? '…' : 'Revoke'}
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─── PAYMENT HISTORY ───────────────────────────────────────────────── */
function PaymentHistory({ payments }) {
  if (!payments.length) {
    return <div style={{ fontSize:13, color:T.dim, textAlign:'center', padding:'20px 0' }}>No payments yet.</div>;
  }
  return (
    <div>
      {payments.map(p => (
        <div key={p.id} style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'10px 14px', marginBottom:6,
          background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8,
        }}>
          <div>
            <div style={{ fontSize:13, color:T.white }}>{p.plan_name || p.description}</div>
            <div style={{ fontSize:11.5, color:T.dim }}>{new Date(p.created_at).toLocaleDateString()}</div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <span style={{ fontFamily:T.mono, fontSize:13, color:T.white }}>{p.amount_display}</span>
            <Badge
              text={p.status}
              color={p.status==='succeeded'?T.green:p.status==='pending'?T.yellow:T.red}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
const handlePortal = async () => {
  setActionLoad(true);
  const res = await apiFetch('/api/subscriptions/portal/', { method:'POST' });
  if (res?.ok) {
    const data = await res.json();
    window.location.href = data.portal_url;   // redirect to Stripe portal
  } else {
    console.error('Portal failed');
  }
  setActionLoad(false);
};
/* ─── MAIN DASHBOARD ────────────────────────────────────────────────── */
  export default function DashboardPage() {
    const { user, subscription, apiFetch, logout, refreshSubscription } = useAuth();
    const navigate = useNavigate();
    const [data,        setData]        = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [actionLoad,  setActionLoad]  = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [tab,         setTab]         = useState('overview');

    const loadDashboard = useCallback(async () => {
    const res = await apiFetch('/api/subscriptions/dashboard/');
    if (res?.ok) setData(await res.json());
    setLoading(false);
  },[apiFetch]);
  const handlePortal = async () => {
     setActionLoad(true);
     const res = await apiFetch('/api/subscriptions/portal/', { method:'POST' });
     if (res?.ok) {
       const data = await res.json();
       window.location.href = data.portal_url;
     }
     setActionLoad(false);
  };
  useEffect(() => { loadDashboard(); }, []);

  const handleCancel = async (atEnd = true) => {
    setActionLoad(true);
    const res = await apiFetch('/api/subscriptions/cancel/', {
      method:'POST', body: JSON.stringify({ at_period_end: atEnd }),
    });
    if (res?.ok) { await refreshSubscription(); await loadDashboard(); }
    setActionLoad(false); setCancelModal(false);
  };

  const handleResume = async () => {
    setActionLoad(true);
    await apiFetch('/api/subscriptions/resume/', { method:'POST' });
    await refreshSubscription(); await loadDashboard();
    setActionLoad(false);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ width:24, height:24, borderRadius:'50%', border:`2px solid ${T.border}`, borderTopColor:T.cyan, animation:'spin .65s linear infinite' }}/>
      </div>
    );
  }

  const sub      = data?.subscription;
  const tier     = sub?.tier || 'free';
  const color    = TIER_COLORS[tier];

  const TABS = [
    { id:'overview', label:'Overview' },
    { id:'billing',  label:'Billing'  },
    { id:'apikeys',  label:'API Keys' },
    { id:'account',  label:'Account'  },
  ];

  return (
    <>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to{transform:rotate(360deg)} }
        input:focus { border-color:rgba(0,200,240,.4)!important; outline:none; }
        ::placeholder { color:${T.dim}!important; }
      `}</style>

      <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.sans }}>

        {/* Top nav */}
        <nav style={{
          borderBottom:`1px solid ${T.border}`,
          padding:'12px 24px', display:'flex',
          alignItems:'center', justifyContent:'space-between',
          background:T.bg2,
        }}>
          <Link to="/" style={{ fontSize:18, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
            Tylent<span style={{ color:T.cyan }}>ed</span>
          </Link>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <Link to="/tools" style={{ fontSize:13, color:T.muted, textDecoration:'none' }}>Tools</Link>
            <div style={{
              width:32, height:32, borderRadius:'50%',
              background:`${color}20`, border:`1px solid ${color}40`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, fontWeight:700, color,
            }}>
              {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
            </div>
          </div>
        </nav>

        <div style={{ maxWidth:860, margin:'0 auto', padding:'28px 24px 48px' }}>

          {/* Page header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
            <div>
              <h1 style={{ fontSize:'clamp(1.4rem,3vw,1.8rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:4 }}>
                Dashboard
              </h1>
              <p style={{ fontSize:13, color:T.muted }}>
                {user?.email} · <Badge text={TIER_LABELS[tier]} color={color}/>
              </p>
            </div>
            <button onClick={handleLogout} style={{
              padding:'7px 16px', borderRadius:7, border:`1px solid ${T.border}`,
              background:'transparent', color:T.muted, fontSize:13, fontFamily:T.sans, cursor:'pointer',
            }}>
              Sign out
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:4, borderBottom:`1px solid ${T.border}`, marginBottom:24 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding:'9px 18px', border:'none',
                borderBottom:`2px solid ${tab===t.id ? T.cyan : 'transparent'}`,
                background:'transparent',
                color: tab===t.id ? T.white : T.muted,
                fontSize:13, fontWeight: tab===t.id ? 600 : 400,
                fontFamily:T.sans, cursor:'pointer', marginBottom:-1, transition:'color .15s',
              }}>{t.label}</button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ──────────────────────────────────────── */}
          {tab === 'overview' && (
            <div>
              <CurrentPlanSection
                subscription={sub}
                onCancel={() => setCancelModal(true)}
                onResume={handleResume}
                loading={actionLoad}
              />

              {/* Stats */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:20 }}>
                <StatCard label="Plan" value={TIER_LABELS[tier]} color={color}/>
                <StatCard label="AI Requests Today" value={data?.ai_usage_today || 0} sub={data?.ai_limit_today ? `of ${data.ai_limit_today} limit` : 'unlimited'} color={T.cyan}/>
                <StatCard label="API Keys" value={data?.api_keys?.length || 0} color={T.yellow}/>
                <StatCard label="Payments" value={data?.payments?.length || 0} color={T.green}/>
              </div>

              {/* Features unlocked */}
              <div style={{ padding:'16px', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>
                  What's unlocked
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:10 }}>
                  {[
                    { label:'All 74+ Tools',    ok:true,                          color:T.green  },
                    { label:'No Ads',           ok:sub?.removes_ads,              color:T.blue   },
                    { label:'AI Tools',         ok:sub?.allows_ai_tools,          color:T.cyan   },
                    { label:'Chrome Extension', ok:sub?.allows_chrome_extension,  color:T.yellow },
                    { label:'API Key Access',   ok:sub?.allows_api_key,           color:T.yellow },
                  ].map(f => (
                    <div key={f.label} style={{
                      padding:'10px 12px', borderRadius:8,
                      background: f.ok ? `${f.color}10` : T.bg3,
                      border:`1px solid ${f.ok ? f.color+'30' : T.border}`,
                      display:'flex', alignItems:'center', gap:8,
                    }}>
                      <span style={{ fontSize:16, color:f.ok?f.color:T.dim }}>{f.ok?'✓':'✗'}</span>
                      <span style={{ fontSize:13, color:f.ok?T.white:T.dim }}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── BILLING TAB ───────────────────────────────────────── */}
{tab === 'billing' && (
  <BillingTab
    subscription={sub}
    payments={data?.payments || []}
    onCancel={() => setCancelModal(true)}
    onResume={handleResume}
    onPortal={handlePortal}
    loading={actionLoad}
  />
)}



          {/* ── API KEYS TAB ──────────────────────────────────────── */}
          {tab === 'apikeys' && (
            <div>
              <SectionHead title="API Keys" action={
                <div style={{ fontSize:12, color:T.dim }}>Max 5 keys</div>
              }/>
              <APIKeysSection
                apiKeys={data?.api_keys || []}
                allowsApiKey={sub?.allows_api_key}
                apiFetch={apiFetch}
                onRefresh={loadDashboard}
              />

              {sub?.allows_api_key && (
                <div style={{ marginTop:20, padding:'14px', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10, fontSize:12, color:T.muted, lineHeight:1.7 }}>
                  <strong style={{ color:T.white }}>How to use your API key:</strong><br/>
                  Add the following header to every request:<br/>
                  <code style={{ display:'block', marginTop:6, padding:'6px 10px', background:T.bg, borderRadius:5, fontFamily:T.mono, color:T.cyan, fontSize:11.5 }}>
                    Authorization: Api-Key arhat_your_key_here
                  </code>
                </div>
              )}
            </div>
          )}

          {/* ── ACCOUNT TAB ───────────────────────────────────────── */}
          {tab === 'account' && (
            <div>
              <SectionHead title="Account Details"/>
              <div style={{ padding:'20px', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, marginBottom:16 }}>
                {[
                  ['Name',  user?.name  || '—'],
                  ['Email', user?.email || '—'],
                  ['Member since', user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'],
                  ['Plan', TIER_LABELS[tier]],
                ].map(([label, value]) => (
                  <div key={label} style={{
                    display:'flex', justifyContent:'space-between',
                    padding:'8px 0', borderBottom:`1px solid ${T.border}`, fontSize:13,
                  }}>
                    <span style={{ color:T.muted }}>{label}</span>
                    <span style={{ color:T.white }}>{value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <button onClick={handleLogout} style={{
                  padding:'9px 18px', borderRadius:8, border:`1px solid ${T.border}`,
                  background:'transparent', color:T.muted, fontSize:13, fontFamily:T.sans, cursor:'pointer',
                }}>Sign out</button>
              </div>
            </div>
          )}
        </div>

        {/* ── CANCEL MODAL ──────────────────────────────────────────── */}
        {cancelModal && (
          <div style={{
            position:'fixed', inset:0, zIndex:100,
            background:'rgba(0,0,0,.7)', backdropFilter:'blur(4px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:24,
          }} onClick={() => setCancelModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              background:T.bg2, border:`1px solid ${T.border}`,
              borderRadius:14, padding:'28px 28px',
              maxWidth:420, width:'100%',
            }}>
              <h3 style={{ fontSize:18, fontWeight:800, color:T.white, marginBottom:8 }}>Cancel subscription?</h3>
              <p style={{ fontSize:13, color:T.muted, lineHeight:1.65, marginBottom:22 }}>
                You'll keep access until the end of your current billing period.
                After that your account returns to the free plan.
              </p>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setCancelModal(false)} style={{
                  flex:1, padding:'10px', borderRadius:8, border:`1px solid ${T.border}`,
                  background:'transparent', color:T.muted, fontSize:13, fontFamily:T.sans, cursor:'pointer',
                }}>Keep Plan</button>
                <button onClick={() => handleCancel(true)} disabled={actionLoad} style={{
                  flex:1, padding:'10px', borderRadius:8, border:`1px solid ${T.red}40`,
                  background:T.red+'12', color:T.red, fontSize:13, fontFamily:T.sans,
                  cursor:'pointer', fontWeight:600,
                }}>
                  {actionLoad ? 'Cancelling…' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
